import { useEffect, useRef } from 'react';
import { isAppDebug } from '@tn/foundation/utils/appDebug';

type UseInspectorKeyboardShortcutParams = {
    onActivate: () => void;
};

/**
 * Checks if the key press event corresponds to the inspector shortcut (Ctrl or Cmd).
 * @param event The keyboard event to check.
 * @returns True if the event is a valid inspector shortcut key press, false otherwise.
 */
const isInspectorShortcutKeyPress = (event: KeyboardEvent) => {
    return !event.repeat && (event.key === 'Control' || event.key === 'Meta');
};

/**
 * Hook that listens for triple Ctrl/Cmd key presses to activate the xstate inspector.
 * Only works when APP_DEBUG is enabled.
 */
const useInspectorKeyboardShortcut = ({ onActivate }: UseInspectorKeyboardShortcutParams) => {
    const ctrlClickCountRef = useRef(0);
    const ctrlClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!isAppDebug()) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isInspectorShortcutKeyPress(event)) {
                return;
            }

            ctrlClickCountRef.current += 1;

            // Reset the count if 1 second has passed since the last key press
            if (ctrlClickTimeoutRef.current) {
                clearTimeout(ctrlClickTimeoutRef.current);
            }

            ctrlClickTimeoutRef.current = setTimeout(() => {
                ctrlClickCountRef.current = 0;
            }, 1000);

            // Activate if Ctrl/Cmd has been pressed 3 times within 1 second
            if (ctrlClickCountRef.current === 3) {
                onActivate();
                ctrlClickCountRef.current = 0;
                if (ctrlClickTimeoutRef.current) {
                    clearTimeout(ctrlClickTimeoutRef.current);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (ctrlClickTimeoutRef.current) {
                clearTimeout(ctrlClickTimeoutRef.current);
            }
        };
    }, [onActivate]);
};

export { useInspectorKeyboardShortcut };
